import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Loader2, Check, Plug, RefreshCw } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

/**
 * RightmoveMappingSection
 * -----------------------
 * Admin → Integrations → Rightmove Fields. Two-column table:
 *   - Left: Rightmove field name + type badge + required badge + description
 *   - Right: dropdown of our Property/CMS fields (or blank).
 * Auto-saves each row change to /api/admin/integrations/rightmove-mapping.
 */
export default function RightmoveMappingSection() {
  const [rmFields, setRmFields] = useState([]);
  const [cmsFields, setCmsFields] = useState([]);
  const [mapping, setMapping] = useState({});  // { RM_field: {value, cms_field} }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKeys, setSavingKeys] = useState({});
  const [savedKeys, setSavedKeys] = useState({});
  const [filter, setFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const savedTimeouts = useRef({});
  const valueDebounces = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/integrations/rightmove-mapping`,
          { withCredentials: true }
        );
        setRmFields(data.rightmove_fields || []);
        setCmsFields(data.cms_fields || []);
        setMapping(data.mapping || {});
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.detail || "Failed to load mapping");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const s = new Set(rmFields.map((f) => f.group || "Other"));
    return ["all", ...Array.from(s)];
  }, [rmFields]);

  const visibleFields = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rmFields.filter((f) => {
      const entry = mapping[f.name] || {};
      if (groupFilter !== "all" && (f.group || "Other") !== groupFilter) return false;
      if (onlyUnmapped && (entry.value || entry.cms_field)) return false;
      if (!q) return true;
      return (
        f.name.toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q) ||
        (entry.cms_field || "").toLowerCase().includes(q) ||
        (entry.value || "").toLowerCase().includes(q)
      );
    });
  }, [rmFields, filter, groupFilter, onlyUnmapped, mapping]);

  const totalMapped = Object.values(mapping).filter(
    (e) => (e?.value || "").trim() || (e?.cms_field || "").trim()
  ).length;

  // Lists of unmapped fields on both sides — surfaced in the summary card so
  // admins can spot coverage gaps at a glance.
  const unmappedRmFields = useMemo(
    () => rmFields.filter((f) => {
      const e = mapping[f.name] || {};
      return !((e.value || "").trim()) && !((e.cms_field || "").trim());
    }),
    [rmFields, mapping]
  );

  const unmappedCmsFields = useMemo(() => {
    const used = new Set(
      Object.values(mapping)
        .map((e) => (e?.cms_field || "").trim())
        .filter(Boolean)
    );
    return cmsFields.filter((c) => !used.has(c.key));
  }, [cmsFields, mapping]);

  // Fire an auto-save patch. `patch` may contain {value?, cms_field?}.
  const persistPatch = async (fieldName, patch) => {
    setSavingKeys((s) => ({ ...s, [fieldName]: true }));
    try {
      const { data } = await axios.put(
        `${API}/admin/integrations/rightmove-mapping`,
        { field: fieldName, ...patch },
        { withCredentials: true }
      );
      // Trust the server's canonical entry.
      const serverEntry = data?.mapping?.[fieldName] ?? { value: "", cms_field: "" };
      setMapping((m) => ({ ...m, [fieldName]: serverEntry }));
      setSavedKeys((s) => ({ ...s, [fieldName]: true }));
      clearTimeout(savedTimeouts.current[fieldName]);
      savedTimeouts.current[fieldName] = setTimeout(() => {
        setSavedKeys((s) => {
          const { [fieldName]: _drop, ...rest } = s;
          return rest;
        });
      }, 1500);
    } catch (e) {
      console.error("mapping save failed:", e);
    } finally {
      setSavingKeys((s) => {
        const { [fieldName]: _drop, ...rest } = s;
        return rest;
      });
    }
  };

  const handleCmsChange = (fieldName, cmsKey) => {
    setMapping((m) => ({
      ...m,
      [fieldName]: { ...(m[fieldName] || { value: "" }), cms_field: cmsKey },
    }));
    persistPatch(fieldName, { cms_field: cmsKey });
  };

  const handleValueChange = (fieldName, newValue) => {
    // Optimistic UI + debounce persistence to avoid spamming the API while
    // typing. 600ms matches the Vosa mapping autosave cadence.
    setMapping((m) => ({
      ...m,
      [fieldName]: { ...(m[fieldName] || { cms_field: "" }), value: newValue },
    }));
    clearTimeout(valueDebounces.current[fieldName]);
    valueDebounces.current[fieldName] = setTimeout(() => {
      persistPatch(fieldName, { value: newValue });
    }, 600);
  };

  // Aggregate autosave state across all rows for a single top-level indicator.
  const anySaving = Object.keys(savingKeys).length > 0;
  const anySaved  = !anySaving && Object.keys(savedKeys).length > 0;
  const autosaveLabel =
    anySaving ? "Saving…"
    : anySaved ? "Saved"
    : "Autosave on";

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Rightmove mapping…
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-6" data-testid="rightmove-mapping-section">
      {/* Coverage summary — surfaces unmapped fields on both sides so admins
          can see which slots still need attention. */}
      <Card data-testid="rm-unmapped-summary">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Coverage Summary</CardTitle>
          <CardDescription>
            Fields on either side that currently have no counterpart. Click a
            Rightmove chip to jump to its row below and pick a CMS field.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Rightmove fields not mapped
              <Badge variant="outline" className="ml-2 text-[10px]">
                {unmappedRmFields.length}
              </Badge>
            </div>
            {unmappedRmFields.length === 0 ? (
              <div className="text-xs text-green-700">All Rightmove fields have a mapping ✓</div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="rm-unmapped-rm-list">
                {unmappedRmFields.map((f) => (
                  <button
                    key={f.name}
                    type="button"
                    onClick={() => {
                      setFilter(f.name);
                      setGroupFilter("all");
                      const row = document.querySelector(`[data-testid="rm-row-${f.name}"]`);
                      row?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    className="inline-flex items-center gap-1 text-[11px] font-mono bg-orange-50 hover:bg-orange-100 text-orange-800 border border-orange-200 rounded px-1.5 py-0.5 transition-colors"
                    title={`${f.group || "Other"} · ${f.type}${f.required ? " · required" : ""}`}
                  >
                    {f.name}
                    {f.required ? <span className="text-red-500">*</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              CMS fields not mapped
              <Badge variant="outline" className="ml-2 text-[10px]">
                {unmappedCmsFields.length}
              </Badge>
            </div>
            {unmappedCmsFields.length === 0 ? (
              <div className="text-xs text-green-700">Every CMS field feeds at least one Rightmove field ✓</div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="rm-unmapped-cms-list">
                {unmappedCmsFields.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center gap-1 text-[11px] font-mono bg-slate-50 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5"
                    title={`${c.label || c.key} · ${c.type}`}
                  >
                    {c.key}
                    <span className="text-[9px] text-slate-500 uppercase">{c.type}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" /> Rightmove Fields
            <span
              className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-muted-foreground"
              data-testid="rightmove-autosave-status"
            >
              {anySaving && <><RefreshCw className="h-3 w-3 animate-spin" /> {autosaveLabel}</>}
              {anySaved  && <><Check className="h-3 w-3 text-emerald-600" /> {autosaveLabel}</>}
              {!anySaving && !anySaved && <span className="opacity-60">{autosaveLabel}</span>}
            </span>
          </CardTitle>
          <CardDescription>
            For each Rightmove field, enter a <span className="font-medium text-foreground">manual value</span> to hard-code the output <em>or</em> pick a <span className="font-medium text-foreground">CMS field</span> from the dropdown to source it per property. If a manual value is set it always wins; otherwise the CMS field is used at push time.
            Each change auto-saves. Currently mapped: <span className="font-medium text-foreground">{totalMapped}</span> / {rmFields.length} fields.
            Any Rightmove field without an existing CMS equivalent is auto-created as a custom CMS field (labelled <span className="font-mono text-xs">rightmove_&lt;field&gt;</span>) so nothing is left unmapped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-medium block mb-1">Search</label>
              <Input
                placeholder="field name, description, or CMS key…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                data-testid="rm-mapping-search"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Group</label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="rm-mapping-group"
              >
                {groups.map((g) => (
                  <option key={g} value={g}>{g === "all" ? "All groups" : g}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm h-10">
              <input
                type="checkbox"
                checked={onlyUnmapped}
                onChange={(e) => setOnlyUnmapped(e.target.checked)}
                data-testid="rm-mapping-only-unmapped"
              />
              Show only unmapped
            </label>
          </div>

          <div className="border rounded-md overflow-hidden">
            <div className="grid grid-cols-[1.4fr_1fr] bg-muted/40 border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <div>Rightmove field</div>
              <div>Manual value  <span className="text-muted-foreground/60 normal-case">OR</span>  CMS field</div>
            </div>
            {visibleFields.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">No fields match your filters.</div>
            )}
            {visibleFields.map((f) => {
              const entry = mapping[f.name] || { value: "", cms_field: "" };
              const cur = entry.cms_field || "";
              const valueStr = entry.value || "";
              const usingValue = valueStr.trim().length > 0;
              // Hide the Manual value input once a CMS field has been picked,
              // mirroring the existing behaviour that hides the CMS dropdown
              // once a manual value is entered.
              const usingCms = !usingValue && cur.trim().length > 0;
              const saving = !!savingKeys[f.name];
              const saved = !!savedKeys[f.name];
              return (
                <div
                  key={f.name}
                  className="grid grid-cols-[1.4fr_1fr] px-4 py-3 border-b last:border-b-0 gap-4 items-start hover:bg-muted/20"
                  data-testid={`rm-row-${f.name}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-sm font-semibold bg-muted px-1.5 py-0.5 rounded">{f.name}</code>
                      <Badge variant="outline" className="text-[10px]">{f.type}</Badge>
                      {f.required && (
                        <Badge className="text-[10px] bg-red-100 text-red-800 border-red-300">Required</Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px]">{f.group}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {f.description}
                    </p>
                    {Array.isArray(f.allowed_values) && f.allowed_values.length > 0 && (
                      <p className="text-[11px] text-muted-foreground/80 mt-1">
                        <span className="font-medium">Allowed:</span> {f.allowed_values.slice(0, 6).join(", ")}
                        {f.allowed_values.length > 6 ? `, +${f.allowed_values.length - 6} more` : ""}
                      </p>
                    )}
                  </div>
                  <div className="min-w-0 space-y-2">
                    {!usingCms && (
                      <div className="flex items-center gap-2 min-w-0" data-testid={`rm-manual-row-${f.name}`}>
                        {Array.isArray(f.allowed_values) && f.allowed_values.length > 0 ? (
                          <select
                            value={valueStr}
                            onChange={(e) => handleValueChange(f.name, e.target.value)}
                            className={`flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm min-w-0 ${usingValue ? "border-green-400 bg-green-50" : ""}`}
                            data-testid={`rm-value-${f.name}`}
                            title="Pick one of the values Rightmove accepts for this field."
                          >
                            <option value="">— Not set —</option>
                            {f.allowed_values.map((av) => (
                              <option key={av} value={av}>{av}</option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            placeholder="Manual value (overrides field)"
                            value={valueStr}
                            onChange={(e) => handleValueChange(f.name, e.target.value)}
                            className={`h-9 text-sm ${usingValue ? "border-green-400 bg-green-50" : ""}`}
                            data-testid={`rm-value-${f.name}`}
                            title="If set, this static value is used instead of the CMS field below."
                          />
                        )}
                        {usingValue && (
                          <Badge className="text-[10px] bg-green-100 text-green-800 border-green-300 whitespace-nowrap">
                            value wins
                          </Badge>
                        )}
                        {usingValue && (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                            {!saving && saved && <Check className="h-4 w-4 text-green-600" />}
                          </div>
                        )}
                      </div>
                    )}
                    {!usingValue && (
                      <div className="flex items-center gap-2 min-w-0">
                        <select
                          value={cur}
                          onChange={(e) => handleCmsChange(f.name, e.target.value)}
                          className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm min-w-0"
                          data-testid={`rm-select-${f.name}`}
                        >
                          <option value="">— unmapped —</option>
                          <optgroup label="Built-in CMS fields">
                            {cmsFields.filter((c) => c.source !== "rightmove").map((c) => (
                              <option key={c.key} value={c.key}>
                                {c.label} ({c.type})
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="Auto-created (Rightmove)">
                            {cmsFields.filter((c) => c.source === "rightmove").map((c) => (
                              <option key={c.key} value={c.key}>
                                {c.label} ({c.type})
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        {cur && cmsFields.find((c) => c.key === cur)?.source === "rightmove" && (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-300 text-amber-800 whitespace-nowrap">
                            auto-created
                          </Badge>
                        )}
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                          {!saving && saved && <Check className="h-4 w-4 text-green-600" />}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
